import { IconButton, Stack, Typography, useMediaQuery } from '@mui/material'
import { FC, useEffect, useState } from 'react'
import { useTheme } from '@mui/material/styles'
import AddIcon from '@mui/icons-material/Add'
import { MenuItem } from 'models'
import { useCurrentStore, useOrdering } from 'hooks'
import { ItemImage } from 'components/ordering/ItemImage'
import { getAdjustedOrderItemPrice } from 'services'

interface DescriptionProps {
  height: number
  webkitLineClamp: number
}

interface MenuItemCardProps {
  item: MenuItem
  onClick?: (item: MenuItem) => void
  unavailableMessage?: string
  viewType?: 'grid' | 'list'
}

export const MenuItemCard: FC<MenuItemCardProps> = ({
  item,
  onClick,
  unavailableMessage,
  viewType = 'list',
}) => {
  const theme = useTheme()
  const { formatCurrency, store } = useCurrentStore()
  const { order } = useOrdering()

  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'))
  const [description, setDescription] = useState<DescriptionProps>({
    height: 42,
    webkitLineClamp: 2,
  })
  useEffect(() => {
    if (isSmDown === false && viewType === 'list') {
      setDescription({ height: 42, webkitLineClamp: 2 })
    } else if (isSmDown === true) {
      setDescription({ height: 50, webkitLineClamp: 3 })
    } else if (
      isSmDown === false &&
      viewType === 'grid' &&
      unavailableMessage
    ) {
      setDescription({ height: 42, webkitLineClamp: 2 })
    } else if (isSmDown === false && viewType === 'grid') {
      setDescription({ height: 62, webkitLineClamp: 3 })
    }
  }, [isSmDown, viewType, setDescription, unavailableMessage])

  return (
    <Stack
      sx={{
        borderRadius: 4,
        padding: 1.5,
        boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
        minWidth: 280,
        backgroundColor: theme.palette.common.white,
        overflow: 'hidden',
        height: 173,
        minHeight: 173,
        gap: 0.5,
        [theme.breakpoints.down('sm')]: {
          borderRadius: 3,
        },
      }}
    >
      <Stack
        sx={{
          flexDirection: 'row',
          justifyContent: 'start',
          alignItems: 'stretch',
          gap: 2,
          height: '100%',
          [theme.breakpoints.down('sm')]: {
            gap: 1.5,
          },
        }}
      >
        <ItemImage src={item.imageURL} />
        <Stack
          sx={{
            flexGrow: 1,
            flexDirection: 'column',
            justifyContent: 'space-between',
            alignItems: 'stretch',
            paddingY: 0.5,
            height: '100%',
          }}
        >
          <Stack sx={{ gap: 0.25 }}>
            <Typography
              variant="h4"
              sx={{
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: store?.show_name_2 && item.name2 ? 1 : 2,
                WebkitBoxOrient: 'vertical',
                color: theme.palette.text.primary,
              }}
            >
              {item.name}
            </Typography>
            {store?.show_name_2 && item.name2 && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: 'vertical',
                  fontWeight: 'bold',
                }}
              >
                {item.name2}
              </Typography>
            )}
          </Stack>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              height: description.height,
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: description.webkitLineClamp,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {item.description}
          </Typography>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography
              variant="h4"
              sx={{
                color: theme.palette.text.primary,
              }}
            >
              {order &&
                formatCurrency(
                  getAdjustedOrderItemPrice(
                    item.price,
                    order.order_type,
                    store,
                  ),
                )}
            </Typography>
            <IconButton
              sx={{
                padding: 0,
                boxShadow: 'none',
                color: theme.palette.common.white,
                width: 40,
                height: 40,

                backgroundColor: theme.palette.success.main,
                '&:hover': {
                  boxShadow: 'none',
                  backgroundColor: theme.palette.success['900'],
                },
              }}
              disabled={
                unavailableMessage !== '' &&
                unavailableMessage !== undefined &&
                unavailableMessage !== null
              }
              onClick={() => onClick && onClick(item)}
            >
              <AddIcon fontSize="large" />
            </IconButton>
          </Stack>
          {unavailableMessage && viewType === 'list' && isSmDown === false && (
            <Typography
              variant="body2"
              color="error"
              sx={{
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 1,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {unavailableMessage}
            </Typography>
          )}
        </Stack>
      </Stack>
      {unavailableMessage && (viewType === 'grid' || isSmDown === true) && (
        <Typography
          variant="body2"
          color="error"
          sx={{
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 1,
            WebkitBoxOrient: 'vertical',
            lineHeight: '1.5rem',
          }}
        >
          {unavailableMessage}
        </Typography>
      )}
    </Stack>
  )
}
